import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  pointerWithin,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import type { CompanyLinkView } from "@portal/schemas";
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
  Label,
  NativeSelect,
  NativeSelectOption,
  cn,
} from "@portal/ui";
import {
  Eye,
  EyeOff,
  Folder as FolderIcon,
  FolderOpen,
  FolderPlus,
  Globe,
  MoreVertical,
  Pencil,
  Plus,
  RefreshCw,
  Save,
  Search,
  SquareArrowOutUpRight,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useForm, useWatch, type UseFormReturn } from "react-hook-form";
import { z } from "zod";
import {
  createCompanyLink,
  refreshCompanyLinkFavicons,
  renameCompanyLinkCategory,
  reorderCompanyLinks,
  updateCompanyLink,
} from "../../api/client";
import { useCompanyLinks, useInvalidate } from "../../api/queries";
import { faviconSrc } from "../../components/BookmarkBar";
import { CardStatus } from "../../components/cards/CardStatus";
import { InlineError } from "../../components/InlineError";
import { DialogTitleWithHint, LabelWithHint } from "../../components/Hint";
import {
  dragKind,
  flattenIds,
  folderDragId,
  folderOfDragId,
  groupLinks,
  linkDragId,
  linkOfDragId,
  matchesQuery,
  moveFolder,
  moveLinkInFolder,
  moveLinkToFolder,
  type Folder,
} from "./bookmark-manager";

const CATEGORY_LIST_ID = "company-link-categories";

/** 追加した直後は favicon がまだ無い(サーバーがあとから取得する)ので、少し待って取り直す。 */
const FAVICON_RECHECK_MS = 4000;

/** ドラッグ中は、いまポインタの下にあるものを優先し、なければ中心が近いものを落とし先にする。 */
const collisionDetection: CollisionDetection = (args) => {
  const within = pointerWithin(args);
  return within.length > 0 ? within : closestCenter(args);
};

/**
 * ブックマークの管理。Chrome の「ブックマーク マネージャ」と同じ体験(ユーザー要望 2026-10-07):
 * 左にフォルダ、右にそのフォルダのブックマーク。上に検索と「⋮」メニュー。変更はその場で保存し(保存ボタンなし)、
 * フォルダ・ブックマークはドラッグで並べ替えられ、ブックマークはフォルダへドラッグして移せる。
 * 「⋮」から、編集・非表示(ホームに出さない。物理削除はしない)ができる。
 */
export function AdminCompanyLinksPage() {
  const links = useCompanyLinks(true);
  const invalidate = useInvalidate();
  const [query, setQuery] = useState("");
  const [selectedName, setSelectedName] = useState<string | null>(null);
  /** まだリンクが入っていないフォルダ(リンクを入れると、保存される) */
  const [extraFolders, setExtraFolders] = useState<string[]>([]);
  /** 並べ替え・移動の保存が終わるまで、手元の結果を見せる(その場で反映されて見える) */
  const [optimistic, setOptimistic] = useState<CompanyLinkView[] | null>(null);
  const [dragging, setDragging] = useState<string | null>(null);
  const [editing, setEditing] = useState<CompanyLinkView | null>(null);
  const [adding, setAdding] = useState(false);
  const [addingFolder, setAddingFolder] = useState(false);
  const [renamingFolder, setRenamingFolder] = useState<string | null>(null);

  const source = useMemo(() => optimistic ?? links.data?.links ?? [], [optimistic, links.data]);
  const folders = useMemo(() => groupLinks(source), [source]);
  const folderNames = useMemo(
    () => [
      ...folders.map((f) => f.name),
      ...extraFolders.filter((n) => !folders.some((f) => f.name === n)),
    ],
    [folders, extraFolders],
  );
  const selected =
    selectedName !== null && folderNames.includes(selectedName)
      ? selectedName
      : (folderNames[0] ?? null);
  const searching = query.trim() !== "";
  const shown = searching
    ? source.filter((l) => matchesQuery(l, query))
    : (folders.find((f) => f.name === selected)?.links ?? []);
  const countOf = (name: string) => folders.find((f) => f.name === name)?.links.length ?? 0;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  /** その場で保存する。結果にかかわらず、サーバーの状態を取り直して手元の仮の結果を捨てる。 */
  const settle = async () => {
    await invalidate.company();
    setOptimistic(null);
  };
  const reorder = useMutation({
    mutationFn: (ids: string[]) => reorderCompanyLinks({ ids }),
    onSettled: settle,
  });
  const move = useMutation({
    mutationFn: (v: { link: CompanyLinkView; folder: string }) =>
      updateCompanyLink(v.link.id, { revision: v.link.revision, category: v.folder }),
    onSettled: settle,
  });
  const toggle = useMutation({
    mutationFn: (l: CompanyLinkView) =>
      updateCompanyLink(l.id, { revision: l.revision, active: !l.active }),
    onSettled: settle,
  });
  const refreshIcons = useMutation({
    mutationFn: refreshCompanyLinkFavicons,
    onSettled: settle,
  });

  const commitFolders = (next: Folder[]) => {
    setOptimistic(next.flatMap((f) => f.links));
    reorder.mutate(flattenIds(next));
  };

  const onDragStart = (e: DragStartEvent) => setDragging(String(e.active.id));
  const onDragEnd = (e: DragEndEvent) => {
    setDragging(null);
    if (!e.over) return;
    const a = String(e.active.id);
    const o = String(e.over.id);
    if (dragKind(a) === "folder") {
      if (dragKind(o) === "folder")
        commitFolders(moveFolder(folders, folderOfDragId(a), folderOfDragId(o)));
      return;
    }
    const link = source.find((l) => l.id === linkOfDragId(a));
    if (!link) return;
    if (dragKind(o) === "folder") {
      // フォルダへドロップ: そのフォルダの末尾へ移す
      const folder = folderOfDragId(o);
      if (folder === link.category) return;
      setOptimistic(moveLinkToFolder(source, link.id, folder));
      move.mutate({ link, folder });
      return;
    }
    const over = source.find((l) => l.id === linkOfDragId(o));
    // 同じフォルダの中での並べ替え
    if (over && over.category === link.category)
      commitFolders(moveLinkInFolder(folders, link.category, link.id, over.id));
  };

  const draggingLink =
    dragging && dragKind(dragging) === "link"
      ? source.find((l) => l.id === linkOfDragId(dragging))
      : undefined;
  const draggingFolder =
    dragging && dragKind(dragging) === "folder" ? folderOfDragId(dragging) : undefined;
  const dndDisabled = searching;

  return (
    <div
      data-testid="admin-company-links-card"
      className="flex h-[calc(100dvh-13rem)] min-h-[28rem] flex-col border"
    >
      {/* 上の帯: タイトル・検索・「⋮」メニュー(Chrome のブックマーク マネージャと同じ並び) */}
      <div className="flex shrink-0 items-center gap-4 border-b px-4 py-2">
        <h2 className="text-base font-semibold">ブックマーク</h2>
        <div className="relative mx-auto w-full max-w-md">
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            type="search"
            aria-label="検索"
            placeholder="ブックマークを検索"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-8"
          />
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon-sm" aria-label="整理">
              <MoreVertical />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-56">
            <DropdownMenuItem onSelect={() => setAdding(true)}>
              <Plus />
              ブックマークを追加
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setAddingFolder(true)}>
              <FolderPlus />
              フォルダを追加
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={() => refreshIcons.mutate()}
              disabled={refreshIcons.isPending}
            >
              <RefreshCw />
              アイコン(favicon)を取得し直す
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {(links.isPending || links.isError) && (
        <div className="px-4 py-3">
          <CardStatus
            isPending={links.isPending}
            isError={links.isError}
            refetch={() => links.refetch()}
          />
        </div>
      )}
      <div className="shrink-0 px-4 empty:hidden">
        <InlineError error={reorder.error ?? move.error ?? toggle.error ?? refreshIcons.error} />
        {refreshIcons.data && (
          <p role="status" className="py-1 text-xs text-muted-foreground">
            アイコンを {refreshIcons.data.updated} 件取得しました
            {refreshIcons.data.failed > 0 &&
              `(${refreshIcons.data.failed} 件は取得できませんでした)`}
            。
          </p>
        )}
      </div>
      {links.data && (
        <DndContext
          sensors={sensors}
          collisionDetection={collisionDetection}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          onDragCancel={() => setDragging(null)}
        >
          <datalist id={CATEGORY_LIST_ID}>
            {folderNames.map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>
          <div className="flex min-h-0 flex-1">
            {/* 左: フォルダ */}
            <nav aria-label="フォルダ" className="w-64 shrink-0 overflow-y-auto border-r py-2">
              <SortableContext
                items={folderNames.map(folderDragId)}
                strategy={verticalListSortingStrategy}
                disabled={dndDisabled}
              >
                <ul className="flex flex-col">
                  {folderNames.map((name) => (
                    <FolderRow
                      key={name}
                      name={name}
                      count={countOf(name)}
                      selected={!searching && name === selected}
                      dropping={draggingLink !== undefined && draggingLink.category !== name}
                      disabled={dndDisabled}
                      onSelect={() => {
                        setQuery("");
                        setSelectedName(name);
                      }}
                      onRename={() => setRenamingFolder(name)}
                    />
                  ))}
                </ul>
              </SortableContext>
              {folderNames.length === 0 && (
                <p className="px-4 py-2 text-sm text-muted-foreground">
                  フォルダはまだありません。
                </p>
              )}
            </nav>
            {/* 右: 選んだフォルダのブックマーク(検索中は、全フォルダから探した結果) */}
            <section
              aria-label={searching ? "検索結果" : (selected ?? "ブックマーク")}
              className="min-w-0 flex-1 overflow-y-auto"
            >
              <div className="sticky top-0 z-10 border-b bg-background px-4 py-2 text-sm font-medium">
                {searching ? `「${query.trim()}」の検索結果` : (selected ?? "")}
                <span className="ml-2 font-normal text-muted-foreground">{shown.length} 件</span>
                {searching && (
                  <span className="ml-3 text-xs font-normal text-muted-foreground">
                    検索中は並べ替えできません。
                  </span>
                )}
              </div>
              <SortableContext
                items={shown.map((l) => linkDragId(l.id))}
                strategy={verticalListSortingStrategy}
                disabled={dndDisabled}
              >
                <ul className="flex flex-col divide-y">
                  {shown.map((l) => (
                    <LinkRow
                      key={l.id}
                      link={l}
                      showFolder={searching}
                      disabled={dndDisabled}
                      onEdit={() => setEditing(l)}
                      onToggle={() => toggle.mutate(l)}
                      toggling={toggle.isPending}
                    />
                  ))}
                </ul>
              </SortableContext>
              {shown.length === 0 && (
                <div className="flex flex-col items-start gap-3 px-4 py-6 text-sm text-muted-foreground">
                  <p>
                    {searching
                      ? "条件に合うブックマークはありません。"
                      : folderNames.length === 0
                        ? "リンクはまだありません。"
                        : "このフォルダは空です。"}
                  </p>
                  {!searching && (
                    <Button variant="outline" size="sm" onClick={() => setAdding(true)}>
                      <Plus />
                      ブックマークを追加
                    </Button>
                  )}
                </div>
              )}
            </section>
          </div>
          {/* つかんでいるものを浮かせて表示する */}
          <DragOverlay>
            {draggingFolder && (
              <div className="flex items-center gap-2 border-2 border-foreground bg-background px-3 py-1.5 text-sm shadow-lg">
                <FolderIcon aria-hidden="true" className="size-4" />
                {draggingFolder}
              </div>
            )}
            {draggingLink && (
              <div className="border-2 border-foreground bg-background shadow-lg">
                <LinkRowBody link={draggingLink} showFolder={false} />
              </div>
            )}
          </DragOverlay>
        </DndContext>
      )}

      <LinkFormDialog
        key={editing?.id ?? (adding ? "new" : "closed")}
        link={editing ?? undefined}
        categories={folderNames}
        defaultCategory={selected ?? undefined}
        open={adding || editing !== null}
        onOpenChange={(open) => {
          if (!open) {
            setAdding(false);
            setEditing(null);
          }
        }}
        onSaved={(link) => setSelectedName(link.category)}
      />
      <AddFolderDialog
        open={addingFolder}
        onOpenChange={setAddingFolder}
        existing={folderNames}
        onAdd={(name) => {
          setExtraFolders((prev) => [...prev, name]);
          setQuery("");
          setSelectedName(name);
        }}
      />
      {renamingFolder !== null && (
        <RenameCategoryDialog
          category={renamingFolder}
          categories={folderNames}
          open
          onOpenChange={(open) => {
            if (!open) setRenamingFolder(null);
          }}
          onRenamed={(to) => {
            setExtraFolders((prev) => prev.map((n) => (n === renamingFolder ? to : n)));
            setSelectedName(to);
          }}
        />
      )}
    </div>
  );
}

/** 左のフォルダ 1 つ。ドラッグで並べ替えられ、ブックマークをドロップするとそのフォルダへ移る。 */
function FolderRow({
  name,
  count,
  selected,
  dropping,
  disabled,
  onSelect,
  onRename,
}: {
  name: string;
  count: number;
  selected: boolean;
  /** ブックマークをつかんでいる間(ドロップできる印を出す) */
  dropping: boolean;
  disabled: boolean;
  onSelect: () => void;
  onRename: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging, isOver } =
    useSortable({ id: folderDragId(name), disabled });
  return (
    <li
      ref={setNodeRef}
      data-testid="folder-row"
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        "group flex items-center gap-2 px-3 py-1.5 text-sm",
        selected ? "bg-accent font-medium" : "hover:bg-accent/60",
        dropping && isOver && "bg-accent ring-2 ring-brand ring-inset",
        isDragging && "opacity-40",
      )}
    >
      <button
        type="button"
        {...attributes}
        {...(disabled ? {} : listeners)}
        onClick={onSelect}
        aria-current={selected ? "true" : undefined}
        aria-label={`フォルダ ${name}(${count} 件)`}
        className="flex min-w-0 flex-1 items-center gap-2 text-left outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {selected ? (
          <FolderOpen aria-hidden="true" className="size-4 shrink-0" />
        ) : (
          <FolderIcon aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
        )}
        <span className="truncate">{name}</span>
        <span className="ml-auto shrink-0 text-xs font-normal text-muted-foreground">{count}</span>
      </button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon-sm"
            className="size-6 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100"
            aria-label={`フォルダ ${name} のメニュー`}
          >
            <MoreVertical />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={onRename}>
            <Pencil />
            名前を変更
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </li>
  );
}

function FaviconImage({ link }: { link: CompanyLinkView }) {
  const [failed, setFailed] = useState(false);
  const src = faviconSrc(link);
  if (!src || failed)
    return <Globe aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />;
  return (
    <img
      src={src}
      alt=""
      width={16}
      height={16}
      onError={() => setFailed(true)}
      className="size-4 shrink-0"
    />
  );
}

/** ブックマーク 1 行の中身(アイコン・名前・URL)。ドラッグ中の見た目にも使う。 */
function LinkRowBody({ link: l, showFolder }: { link: CompanyLinkView; showFolder: boolean }) {
  return (
    <div
      className={cn("flex min-w-0 flex-1 items-center gap-3", !l.active && "text-muted-foreground")}
    >
      <FaviconImage link={l} />
      <div className="min-w-0 flex-1">
        <a
          href={l.url}
          target="_blank"
          rel="noopener noreferrer"
          className="block truncate text-sm font-medium hover:underline"
        >
          {l.title}
        </a>
        <p className="truncate text-xs text-muted-foreground">
          {l.url.replace(/^https?:\/\//, "")}
          {l.note && ` — ${l.note}`}
        </p>
      </div>
      {showFolder && (
        <Badge variant="outline" className="shrink-0 font-normal">
          {l.category}
        </Badge>
      )}
      {!l.active && (
        <Badge variant="secondary" className="shrink-0">
          非表示
        </Badge>
      )}
    </div>
  );
}

/** 右のブックマーク 1 行。行をつかんで並べ替え・フォルダへ移動、「⋮」から編集・非表示。 */
function LinkRow({
  link: l,
  showFolder,
  disabled,
  onEdit,
  onToggle,
  toggling,
}: {
  link: CompanyLinkView;
  showFolder: boolean;
  disabled: boolean;
  onEdit: () => void;
  onToggle: () => void;
  toggling: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: linkDragId(l.id),
    disabled,
  });
  return (
    <li
      ref={setNodeRef}
      data-testid="link-row"
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        "group flex items-center gap-2 px-4 py-2 hover:bg-accent/40",
        isDragging && "opacity-40",
      )}
      {...attributes}
      {...(disabled ? {} : listeners)}
      aria-label={`${l.title}${disabled ? "" : "(ドラッグで並べ替え)"}`}
    >
      <LinkRowBody link={l} showFolder={showFolder} />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={`${l.title} のメニュー`}
            // 行のドラッグを始めずに、メニューを開く
            onPointerDown={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <MoreVertical />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-48">
          <DropdownMenuItem onSelect={onEdit}>
            <Pencil />
            編集
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <a href={l.url} target="_blank" rel="noopener noreferrer">
              <SquareArrowOutUpRight />
              新しいタブで開く
            </a>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={onToggle} disabled={toggling}>
            {l.active ? <EyeOff /> : <Eye />}
            {l.active ? "非表示にする" : "表示する"}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </li>
  );
}

const folderSchema = z.object({ name: z.string().trim().min(1, "フォルダ名は必須です").max(50) });
type FolderValues = z.infer<typeof folderSchema>;

/**
 * フォルダを追加する。フォルダはリンクが入って初めて保存される(中身が空のあいだは、この画面だけに出る)。
 */
function AddFolderDialog({
  open,
  onOpenChange,
  existing,
  onAdd,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  existing: string[];
  onAdd: (name: string) => void;
}) {
  const form = useForm<FolderValues>({
    resolver: zodResolver(folderSchema),
    values: { name: "" },
  });
  const name = useWatch({ control: form.control, name: "name" }).trim();
  const duplicated = existing.includes(name);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form
          onSubmit={form.handleSubmit((v) => {
            if (existing.includes(v.name)) return;
            onAdd(v.name);
            form.reset();
            onOpenChange(false);
          })}
          noValidate
          className="flex flex-col gap-4"
        >
          <DialogHeader>
            <DialogTitleWithHint hint="最初のブックマークを入れると保存されます。中身が空のあいだは、この画面にだけ出ます。">
              フォルダを追加
            </DialogTitleWithHint>
          </DialogHeader>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="folder-name">フォルダ名</Label>
            <Input id="folder-name" autoFocus {...form.register("name")} />
            {form.formState.errors.name && (
              <p className="text-xs text-destructive">{form.formState.errors.name.message}</p>
            )}
            {duplicated && (
              <p className="text-xs text-destructive">同じ名前のフォルダがあります。</p>
            )}
          </div>
          <DialogFooter>
            <Button type="submit" disabled={duplicated}>
              <Save />
              保存
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const renameSchema = z.object({ to: z.string().trim().min(1, "フォルダ名は必須です").max(50) });
type RenameValues = z.infer<typeof renameSchema>;

function RenameCategoryDialog({
  category,
  categories,
  open,
  onOpenChange,
  onRenamed,
}: {
  category: string;
  categories: string[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRenamed?: (to: string) => void;
}) {
  const invalidate = useInvalidate();
  const form = useForm<RenameValues>({
    resolver: zodResolver(renameSchema),
    values: { to: category },
  });
  // form.watch は React Compiler と相性が悪いので useWatch を使う
  const to = useWatch({ control: form.control, name: "to" }).trim();
  const willMerge = to !== "" && to !== category && categories.includes(to);
  const save = useMutation({
    mutationFn: (v: RenameValues) => renameCompanyLinkCategory({ from: category, to: v.to }),
    onSuccess: async (_data, v) => {
      await invalidate.company();
      onRenamed?.(v.to);
      onOpenChange(false);
    },
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form
          onSubmit={form.handleSubmit((v) => save.mutate(v))}
          noValidate
          className="flex flex-col gap-4"
        >
          <DialogHeader>
            <DialogTitleWithHint
              hint={`「${category}」のリンクすべてに新しいフォルダ名を付けます。既にあるフォルダ名にすると、そのフォルダの末尾に統合されます。`}
            >
              フォルダ名を変更
            </DialogTitleWithHint>
          </DialogHeader>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="rename-to">新しいフォルダ名</Label>
            <Input id="rename-to" list={CATEGORY_LIST_ID} {...form.register("to")} />
            {form.formState.errors.to && (
              <p className="text-xs text-destructive">{form.formState.errors.to.message}</p>
            )}
            {willMerge && (
              <p className="text-xs text-muted-foreground">
                既存の「{to}」に統合します(元には戻せません)。
              </p>
            )}
          </div>
          <InlineError error={save.error} />
          <DialogFooter>
            <Button
              type="submit"
              variant={willMerge ? "destructive" : "default"}
              disabled={save.isPending}
            >
              <Save />
              {willMerge ? "統合する" : "保存"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** 空のフォルダはサーバー側で「その他」になる(SPEC §13.4)。選択肢にも必ず出す。 */
const DEFAULT_CATEGORY = "その他";
/** 「新しいフォルダを作る」を選んだことを表す値。フォルダ名としては使わない。 */
const NEW_CATEGORY = "__new__";

/**
 * フォルダの入力。既存のフォルダから選ぶか、「新しいフォルダを作る」で自由入力に切り替える
 * (ユーザー要望 2026-09-16)。表記揺れを防ぐため、既存があるときは選択を既定にする。
 */
function CategoryField({
  form,
  categories,
  current,
}: {
  form: UseFormReturn<LinkValues>;
  categories: string[];
  current: string | undefined;
}) {
  const category = useWatch({ control: form.control, name: "category" });
  const options = [...new Set([DEFAULT_CATEGORY, ...categories, ...(current ? [current] : [])])];
  // 一覧にない名前(新しく作る途中)なら自由入力の状態
  const typing = category !== "" && !options.includes(category);
  const [creating, setCreating] = useState(typing);
  if (creating || typing) {
    return (
      <div className="flex gap-2">
        <Input
          id="link-category"
          autoFocus
          placeholder="新しいフォルダ名"
          {...form.register("category")}
        />
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            setCreating(false);
            form.setValue("category", current ?? DEFAULT_CATEGORY);
          }}
        >
          一覧から選ぶ
        </Button>
      </div>
    );
  }
  return (
    <NativeSelect
      id="link-category"
      className="w-full"
      value={category === "" ? DEFAULT_CATEGORY : category}
      onChange={(e) => {
        if (e.target.value === NEW_CATEGORY) {
          setCreating(true);
          form.setValue("category", "");
          return;
        }
        form.setValue("category", e.target.value);
      }}
    >
      {options.map((c) => (
        <NativeSelectOption key={c} value={c}>
          {c}
        </NativeSelectOption>
      ))}
      <NativeSelectOption value={NEW_CATEGORY}>新しいフォルダを作る…</NativeSelectOption>
    </NativeSelect>
  );
}

const linkSchema = z.object({
  title: z.string().trim().min(1, "名前は必須です").max(200),
  url: z.string().trim().min(1, "URL は必須です").max(2000),
  category: z.string().trim().max(50),
  note: z.string().trim().max(500),
});
type LinkValues = z.infer<typeof linkSchema>;

/** ブックマークの追加・編集(Chrome の「ブックマークを編集」と同じく、名前と URL が中心)。 */
function LinkFormDialog({
  link,
  categories,
  defaultCategory,
  open,
  onOpenChange,
  onSaved,
}: {
  link?: CompanyLinkView | undefined;
  categories: string[];
  /** 追加するときの、フォルダの初期値(いま開いているフォルダ) */
  defaultCategory?: string | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved?: (link: CompanyLinkView) => void;
}) {
  const invalidate = useInvalidate();
  const form = useForm<LinkValues>({
    resolver: zodResolver(linkSchema),
    values: {
      title: link?.title ?? "",
      url: link?.url ?? "",
      category: link?.category ?? defaultCategory ?? "",
      note: link?.note ?? "",
    },
  });
  const save = useMutation({
    mutationFn: (v: LinkValues) =>
      link ? updateCompanyLink(link.id, { ...v, revision: link.revision }) : createCompanyLink(v),
    onSuccess: async ({ link: saved }) => {
      await invalidate.company();
      // favicon はサーバーが、返事のあとに取得する。少し待ってから取り直して、画面に出す
      setTimeout(() => void invalidate.company(), FAVICON_RECHECK_MS);
      onSaved?.(saved);
      onOpenChange(false);
      if (!link) form.reset();
    },
  });
  const errors = form.formState.errors;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form
          onSubmit={form.handleSubmit((v) => save.mutate(v))}
          noValidate
          className="flex flex-col gap-4"
        >
          <DialogHeader>
            <DialogTitle>{link ? "ブックマークを編集" : "ブックマークを追加"}</DialogTitle>
            <DialogDescription className="sr-only">
              名前・URL・フォルダ・説明を入力します。
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="link-title">名前</Label>
            <Input id="link-title" autoFocus {...form.register("title")} />
            {errors.title && <p className="text-xs text-destructive">{errors.title.message}</p>}
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="link-url">URL</Label>
            <Input id="link-url" type="url" {...form.register("url")} />
            {errors.url && <p className="text-xs text-destructive">{errors.url.message}</p>}
          </div>
          <div className="flex flex-col gap-1.5">
            <LabelWithHint
              htmlFor="link-category"
              hint="既存の名前から選ぶか、新しい名前を入力します(空なら「その他」)。そのフォルダの末尾に置かれます。"
            >
              フォルダ
            </LabelWithHint>
            <CategoryField form={form} categories={categories} current={link?.category} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="link-note">説明</Label>
            <Input id="link-note" {...form.register("note")} />
          </div>
          <InlineError error={save.error} />
          <DialogFooter>
            <Button type="submit" disabled={save.isPending}>
              <Save />
              保存
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
